import { effect, inject, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, switchMap, tap, catchError, of, forkJoin, BehaviorSubject, Observable, map } from 'rxjs';
import { FilteringService } from 'src/app/shared/services/filtering.service';
import { ModuleService } from 'src/app/shared/services/module.service';
import { ModuleItem, ModuleMatrix, ModuleMatrixRow, ModuleMatrixSearchEvent, ModuleModel } from '../module-model';
import { SnackbarService } from 'ok-angular-shared/components';
import { PagedResult } from 'src/app/shared/models/paging';
import { PagerBase } from 'src/app/shared/models/pagerBase';

interface ModulesListFilterState {
  query: string | null;
  organizationId: number | null;
  sort: Sort | null;
  page: PageEvent;
}
export enum ModulesListQueryParams {
  query = 'query',
  organizationId = 'organizationId',
}
export class ModuleListStateService extends PagerBase {
  activatedRoute = inject(ActivatedRoute);
  moduleService = inject(ModuleService);
  router = inject(Router);
  pagingOptions = signal<number[]>(this.pageSizes);
  filteringService = inject(FilteringService);
  readonly queryParamMap$ = toSignal(this.activatedRoute.queryParamMap);
  public loading: WritableSignal<boolean> = signal<boolean>(false);
  private snackbarService = inject(SnackbarService);
  private refreshTrigger = signal(0);
  public filters: WritableSignal<ModulesListFilterState> = signal<ModulesListFilterState>({
    query: this.queryParamMap$()?.get(ModulesListQueryParams.query)
      ? decodeURIComponent(this.queryParamMap$()?.get(ModulesListQueryParams.query) ?? '')
      : null,
    organizationId: this.queryParamMap$()?.get(ModulesListQueryParams.organizationId)
      ? parseInt(this.queryParamMap$()?.get(ModulesListQueryParams.organizationId) ?? '0')
      : null,
    sort: null,
    page: {
      length: 0,
      pageIndex: 0,
      pageSize: this.defaultPageSize,
    },
  });
  data$: Observable<{ moduleMatrixRows: ModuleMatrixRow[]; modules: ModuleModel[]; totalCount: number }> = combineLatest([
    toObservable(this.filters),
    toObservable(this.refreshTrigger),
  ]).pipe(
    tap(() => this.loading.set(true)),
    switchMap(([filters]) => {
      const event = <ModuleMatrixSearchEvent>{
        query: filters.query,
        sorting: filters.sort,
        paging: filters.page,
        organizationId: filters.organizationId,
      };

      return forkJoin({
        modules: this.moduleService.getModules(),
        moduleMatrix: this.moduleService.getModuleMatrix(event),
      });
    }),
    catchError(error => {
      if (error.error.code === 100906) {
        this.snackbarService.warning('Modul ikke fundet');
      }
      return of({
        modules: [] as ModuleModel[],
        moduleMatrix: { items: [] as ModuleMatrix[], totalCount: 0, pageIndex: 0, pageSize: this.defaultPageSize },
        totalCount: 0,
      });
    }),
    tap(() => this.loading.set(false)),
    map(({ modules, moduleMatrix }) => {
      const rows = new Array<ModuleMatrixRow>();

      moduleMatrix.items.forEach(organization => {
        const moduleItems: ModuleItem[] = [];
        modules.forEach(async module => {
          moduleItems.push({
            module: module,
            organizationHasModule: this.organizationHasModule(moduleMatrix, organization.organizationId, module.id),
            updating: new BehaviorSubject<boolean>(false),
          });
        });

        rows.push({
          modules: moduleItems,
          organizationId: organization.organizationId,
          organizationName: organization.organizationName,
          organizationType: organization.organizationType,
        });
      });
      return { moduleMatrixRows: rows, modules: modules, totalCount: moduleMatrix.totalCount };
    })
  );
  constructor() {
    super();
    effect(() => {
      const filters = this.filters();
      const queryParams: Record<ModulesListQueryParams, string | null> = {
        [ModulesListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
        [ModulesListQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }
  public refresh() {
    this.refreshTrigger.update(x => x + 1);
  }

  private organizationHasModule(matrix: PagedResult<ModuleMatrix>, organizationId: number, moduleId: number): boolean {
    return matrix.items.some(m => m.organizationId === organizationId && m.moduleIds.includes(moduleId));
  }
}
